/**
 * Centralized API Configuration
 * Single source of truth for API URL and common fetch utilities
 * 
 * Security: Uses HttpOnly cookies for refresh tokens (XSS-safe)
 * - Access tokens: Stored in memory (React state)
 * - Refresh tokens: Stored in HttpOnly cookies (automatic with credentials: 'include')
 */

import { showError, showWarning, parseBackendError, showToast } from '../utils/errorHandler';

// Base API URL from environment
export const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://demo.acusightpro.com';
export const API_URL = `${API_BASE_URL}/api`;

// In-memory access token storage (more secure than localStorage)
let accessToken = null;

/**
 * Set the access token in memory
 */
export const setAccessToken = (token) => {
  accessToken = token;
  // Also set in localStorage for backward compatibility during migration
  if (token) {
    localStorage.setItem('asp_token', token);
  }
};

/**
 * Get the current access token
 */
export const getAccessToken = () => {
  // Try memory first, then localStorage for backward compatibility
  return accessToken || localStorage.getItem('asp_token');
};

/**
 * Clear the access token
 */
export const clearAccessToken = () => {
  accessToken = null;
  localStorage.removeItem('asp_token');
  localStorage.removeItem('asp_user');
};

// Default headers for JSON requests
export const JSON_HEADERS = {
  'Content-Type': 'application/json',
};

/**
 * Get authorization headers with JWT token
 */
export const getAuthHeaders = () => {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Combine headers with auth
 */
export const getHeaders = (includeAuth = true) => ({
  ...JSON_HEADERS,
  ...(includeAuth ? getAuthHeaders() : {}),
});

/**
 * Standard API response handler with global error handling
 */
export const handleResponse = async (response, showErrors = true) => {
  if (!response.ok) {
    let error;
    try {
      error = await response.json();
    } catch (e) {
      error = { detail: 'Request failed' };
    }
    
    // Parse the error using our enhanced error parser
    const parsed = parseBackendError(error);
    
    // Handle specific status codes globally
    if (response.status === 401 && showErrors) {
      showToast('Your session has expired. Please log in again.', 'warning', 5000, {
        actionLabel: 'Log In',
        onAction: () => { window.location.href = '/login'; }
      });
      clearAccessToken();
      setTimeout(() => { window.location.href = '/login'; }, 2000);
      return null;
    }
    
    if (response.status === 403 && showErrors) {
      showError(parsed.message || 'You do not have permission to perform this action.', {
        action: parsed.action
      });
    }
    
    if (response.status === 404 && showErrors) {
      showError(parsed.message || 'The requested resource was not found.', {
        action: parsed.action
      });
    }
    
    if (response.status === 429 && showErrors) {
      const retryAfter = error?.error?.metadata?.retry_after || 60;
      showWarning(`Too many requests. Please wait ${retryAfter} seconds.`, {
        action: parsed.action
      });
    }
    
    if (response.status >= 500 && showErrors) {
      showError(parsed.message || 'Server error. Please try again later.', {
        action: parsed.action || 'If the problem persists, contact support.'
      });
    }
    
    // Throw error with parsed message
    const err = new Error(parsed.message || error.detail || `HTTP ${response.status}`);
    err.parsed = parsed;
    err.status = response.status;
    throw err;
  }
  return response.json();
};

/**
 * API fetch wrapper with error handling
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @param {boolean} options.showErrors - Whether to show error toasts (default: true)
 * @param {number} options.timeout - Request timeout in milliseconds (default: 60000, AI: 120000)
 */
export const apiFetch = async (endpoint, options = {}) => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  const { showErrors = true, timeout = 60000, ...fetchOptions } = options;  // Increased default to 60s
  
  const config = {
    headers: getHeaders(fetchOptions.auth !== false),
    credentials: 'include',  // Include cookies for HttpOnly refresh token
    ...fetchOptions,
  };

  // Don't set Content-Type for FormData
  if (fetchOptions.body instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  config.signal = controller.signal;

  try {
    const response = await fetch(url, config);
    clearTimeout(timeoutId);
    return handleResponse(response, showErrors);
  } catch (error) {
    clearTimeout(timeoutId);
    // Network errors
    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      if (showErrors) {
        showError('Unable to connect to server. Please check your internet connection.', {
          action: 'Check your connection and try again.'
        });
      }
    }
    // Timeout errors - show as info instead of warning to be less alarming
    if (error.name === 'AbortError') {
      // Silently fail for timeout - don't show error toast
      console.log('Request timeout - will retry or use cached data');
    }
    throw error;
  }
};

/**
 * Convenience methods
 */
export const api = {
  get: (endpoint, options = {}) => 
    apiFetch(endpoint, { method: 'GET', ...options }),
  
  post: (endpoint, data, options = {}) => 
    apiFetch(endpoint, { 
      method: 'POST', 
      body: JSON.stringify(data), 
      ...options 
    }),
  
  put: (endpoint, data, options = {}) => 
    apiFetch(endpoint, { 
      method: 'PUT', 
      body: JSON.stringify(data), 
      ...options 
    }),
  
  patch: (endpoint, data, options = {}) => 
    apiFetch(endpoint, { 
      method: 'PATCH', 
      body: JSON.stringify(data), 
      ...options 
    }),
  
  delete: (endpoint, options = {}) => 
    apiFetch(endpoint, { method: 'DELETE', ...options }),
  
  // Special method for AI endpoints with longer timeout (120 seconds)
  aiPost: (endpoint, data, options = {}) =>
    apiFetch(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      timeout: 120000,  // 120 seconds for AI calls (enhanced docs can be long)
      ...options
    }),
};

export default api;
